import React, { createContext, useContext, useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anonymous, obj = signed
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Sincronizare sesiune: reîncarcă profilul (permisiuni, alocări) la focus și periodic
  useEffect(() => {
    let active = true;
    async function refreshMe() {
      try {
        const { data } = await api.get("/auth/me");
        if (active) setUser((prev) => (prev && typeof prev === "object" ? data : prev));
      } catch (e) {
        const s = e?.response?.status;
        if (active && (s === 401 || s === 403)) setUser(false);
      }
    }
    const timer = setInterval(refreshMe, 60000);
    const onFocus = () => { if (document.visibilityState !== "hidden") refreshMe(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  async function login(email, password) {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: apiErr(e) };
    }
  }

  function hasPermission(perm) {
    if (!user || typeof user !== "object") return false;
    if (user.role === "admin") return true;
    return (user.permissions || []).includes(perm);
  }

  async function logout() {
    try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
    setUser(false);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
